import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QRCodeSVG } from "qrcode.react";
import { api, currentUser } from "@/lib/api";
import { ShieldCheck, ShieldOff, RefreshCw, KeyRound, MonitorSmartphone, Download, Printer, Activity } from "lucide-react";

const err = (e) => toast.error(e.response?.data?.detail || "Request failed");

function RecoveryCodesView({ codes, onDone }) {
  const download = () => {
    const blob = new Blob([`KARMIVO SUPER ADMIN — RECOVERY CODES\nKeep these safe. Each code can be used once.\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "karmivo-recovery-codes.txt";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  return (
    <Card className="admin-card p-5 border-amber-500/40" data-testid="recovery-codes-panel">
      <div className="font-bold text-amber-300 mb-1">SAVE YOUR RECOVERY CODES</div>
      <p className="text-xs text-slate-400 mb-3">These codes can be used if you lose access to Google Authenticator. They are shown <b>only once</b>. Each code works exactly once.</p>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-950 rounded-lg p-4 mb-3">
        {codes.map((c, i) => <div key={i} className="text-slate-200" data-testid={`recovery-code-${i}`}>{c}</div>)}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={download} data-testid="recovery-download-btn"><Download size={14} className="mr-1"/>Download</Button>
        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => window.print()} data-testid="recovery-print-btn"><Printer size={14} className="mr-1"/>Print</Button>
        <Button size="sm" className="bg-orange-500 hover:bg-orange-600 ml-auto" onClick={onDone} data-testid="recovery-done-btn">I've saved them</Button>
      </div>
    </Card>
  );
}

function MfaWizard({ mode, onClose, onDone }) {
  // mode: "enable" | "change"
  const [step, setStep] = useState(1);
  const [pw, setPw] = useState("");
  const [curCode, setCurCode] = useState("");      // change mode: current authenticator code
  const [setup, setSetup] = useState(null);
  const [newCode, setNewCode] = useState("");
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    if (!pw) return toast.error("Enter your current password");
    if (mode === "change" && !curCode) return toast.error("Enter the code from your current authenticator");
    setBusy(true);
    try {
      const payload = mode === "change" ? { current_password: pw, code: curCode } : { current_password: pw };
      const url = mode === "change" ? "/admin/security/mfa/change/start" : "/admin/security/mfa/setup";
      const { data } = await api.post(url, payload);
      setSetup(data); setStep(2);
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!newCode.trim()) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      const url = mode === "change" ? "/admin/security/mfa/change/confirm" : "/admin/security/mfa/enable";
      const { data } = await api.post(url, { current_password: pw, code: newCode.trim() });
      setCodes(data.recovery_codes || []);
      setStep(3);
      toast.success(mode === "change" ? "Authenticator changed" : "Two-factor authentication has been enabled successfully.");
    } catch (e) { err(e); } finally { setBusy(false); }
  };

  if (step === 3 && codes) return <RecoveryCodesView codes={codes} onDone={() => { onDone(); onClose(); }}/>;

  return (
    <Card className="admin-card p-5 border-orange-500/40" data-testid="mfa-wizard">
      <div className="font-bold mb-3">{mode === "change" ? "Change Google Authenticator" : "Enable Two-Factor Authentication"}</div>
      {step === 1 && (
        <div className="space-y-3">
          <div>
            <Label className="text-slate-300 text-xs">Step 1 — Confirm current password</Label>
            <Input type="password" value={pw} onChange={e => setPw(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="mfa-wizard-pw"/>
          </div>
          {mode === "change" && (
            <div>
              <Label className="text-slate-300 text-xs">Step 2 — Code from your CURRENT authenticator</Label>
              <Input value={curCode} onChange={e => setCurCode(e.target.value)} placeholder="123456" className="bg-slate-900 border-slate-700 font-mono" maxLength={8} data-testid="mfa-wizard-current-code"/>
            </div>
          )}
          <div className="flex gap-2">
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={startSetup} disabled={busy} data-testid="mfa-wizard-continue">{busy ? "…" : "Continue"}</Button>
            <Button variant="ghost" className="text-slate-400" onClick={onClose} data-testid="mfa-wizard-cancel">Cancel</Button>
          </div>
        </div>
      )}
      {step === 2 && setup && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400">Step {mode === "change" ? 3 : 2} — Install Google Authenticator (or Microsoft Authenticator / Authy), then scan this QR code:</p>
          <div className="bg-white rounded-lg p-4 w-fit mx-auto" data-testid="mfa-qr">
            <QRCodeSVG value={setup.otpauth_uri} size={180}/>
          </div>
          <div className="text-center">
            <div className="text-xs text-slate-500">Can't scan the QR code?</div>
            <div className="font-mono text-sm text-orange-300 select-all" data-testid="mfa-manual-key">{setup.manual_key}</div>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Step {mode === "change" ? 4 : 3} — Enter the 6-digit code shown in Google Authenticator</Label>
            <Input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="123456" className="bg-slate-900 border-slate-700 font-mono text-center text-lg tracking-[0.4em]" maxLength={8} data-testid="mfa-wizard-new-code"/>
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={confirm} disabled={busy} data-testid="mfa-wizard-verify">{busy ? "…" : "Verify & Enable"}</Button>
            <Button variant="ghost" className="text-slate-400" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function DisableMfa({ onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [code, setCode] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!pw || !code || !confirm) return toast.error("Password, code and confirmation are all required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/security/mfa/disable", { current_password: pw, code, confirm: true });
      toast.success("Two-factor authentication disabled. " + (data.note || ""));
      onDone(); onClose();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <Card className="admin-card p-5 border-red-500/40" data-testid="mfa-disable-panel">
      <div className="font-bold text-red-300 mb-2">Disable Two-Factor Authentication</div>
      <p className="text-xs text-slate-400 mb-3">This reduces account security. All active sessions will be signed out.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label className="text-slate-300 text-xs">Current Password</Label><Input type="password" value={pw} onChange={e => setPw(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="mfa-disable-pw"/></div>
        <div><Label className="text-slate-300 text-xs">Authenticator code or recovery code</Label><Input value={code} onChange={e => setCode(e.target.value)} className="bg-slate-900 border-slate-700 font-mono" data-testid="mfa-disable-code"/></div>
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-slate-300">
        <input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)} data-testid="mfa-disable-confirm"/>
        I understand this removes the second authentication factor from the Super Admin account.
      </label>
      <div className="flex gap-2 mt-3">
        <Button className="bg-red-600 hover:bg-red-700" onClick={submit} disabled={busy} data-testid="mfa-disable-submit">{busy ? "…" : "Disable MFA"}</Button>
        <Button variant="ghost" className="text-slate-400" onClick={onClose}>Cancel</Button>
      </div>
    </Card>
  );
}

function SessionsPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/security/sessions").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const revoke = async (jti) => {
    try { await api.post(`/admin/security/sessions/${jti}/revoke`); toast.success("Session revoked"); load(); }
    catch (e) { err(e); }
  };
  return (
    <Card className="admin-card p-5" data-testid="sessions-panel">
      <div className="font-bold mb-3">Active Sessions</div>
      {items.length === 0 && <div className="text-sm text-slate-500">No active sessions found.</div>}
      <div className="space-y-2">
        {items.map(s => (
          <div key={s.jti} className="flex items-center justify-between text-sm border-b border-slate-800 pb-2" data-testid={`session-${s.jti.slice(0,8)}`}>
            <div>
              <div className="font-mono text-xs text-slate-300">{s.jti.slice(0, 8)}… · {s.ip || "unknown ip"}</div>
              <div className="text-xs text-slate-500 truncate max-w-md">{s.user_agent || "unknown device"}</div>
              <div className="text-[10px] text-slate-600">started {s.created_at}</div>
            </div>
            <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={() => revoke(s.jti)} data-testid={`revoke-${s.jti.slice(0,8)}`}>Revoke</Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function SecurityPage() {
  const me = currentUser() || {};
  const [status, setStatus] = useState(null);
  const [wizard, setWizard] = useState(null);     // "enable" | "change" | null
  const [disabling, setDisabling] = useState(false);
  const [regen, setRegen] = useState(false);
  const [regenPw, setRegenPw] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [regenCodes, setRegenCodes] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [activity, setActivity] = useState([]);
  const [showActivity, setShowActivity] = useState(false);
  // password change
  const [curPw, setCurPw] = useState(""); const [newPw, setNewPw] = useState(""); const [newPw2, setNewPw2] = useState("");
  // identity change
  const [email, setEmail] = useState(me.email || "");
  const [emailPw, setEmailPw] = useState("");
  const [phone, setPhone] = useState(me.phone || "");
  const [phonePw, setPhonePw] = useState("");

  const changeEmail = async () => {
    if (!emailPw) return toast.error("Enter your current password to confirm");
    try {
      const { data } = await api.post("/admin/security/change-email", { new_email: email.trim(), current_password: emailPw });
      localStorage.setItem("karmivo_user", JSON.stringify({ ...me, email: data.email }));
      setEmailPw(""); toast.success("Email updated. Use the new email for future logins.");
    } catch (e) { err(e); }
  };

  const changePhone = async () => {
    if (!phonePw) return toast.error("Enter your current password to confirm");
    try {
      const { data } = await api.post("/admin/security/change-phone", { new_phone: phone.trim(), current_password: phonePw });
      localStorage.setItem("karmivo_user", JSON.stringify({ ...me, phone: data.phone }));
      setPhonePw(""); toast.success("Mobile number updated.");
    } catch (e) { err(e); }
  };

  const load = () => api.get("/admin/security/status").then(r => setStatus(r.data)).catch(e => err(e));
  useEffect(() => { load(); }, []);
  if (!status) return <div className="text-slate-400">Loading…</div>;

  const doRegen = async () => {
    try {
      const { data } = await api.post("/admin/security/recovery-codes/regenerate", { current_password: regenPw, code: regenCode });
      setRegenCodes(data.recovery_codes); toast.success("New recovery codes generated — all previous codes are now invalid.");
    } catch (e) { err(e); }
  };

  const loadActivity = async () => {
    try {
      const { data } = await api.get("/admin/audit");
      setActivity(data.filter(a => a.action.startsWith("auth.") || a.action.startsWith("security.")).slice(0, 30));
      setShowActivity(true);
    } catch (e) { err(e); }
  };

  const changePassword = async () => {
    if (!curPw || !newPw) return toast.error("Fill current and new password");
    if (newPw !== newPw2) return toast.error("New passwords do not match");
    try {
      await api.post("/admin/security/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password changed. All sessions signed out — please sign in again.");
      setTimeout(() => { localStorage.clear(); window.location.href = "/admin/login"; }, 1500);
    } catch (e) { err(e); }
  };

  const Dot = ({ ok }) => <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${ok ? "bg-emerald-400" : "bg-slate-600"}`}/>;

  return (
    <div data-testid="security-settings-page">
      <h1 className="font-display text-3xl font-bold mb-1">Account Security</h1>
      <p className="text-sm text-slate-400 mb-5">Super Admin security controls. Every change is audit-logged.</p>

      <Card className="admin-card p-5 mb-4" data-testid="security-status-card">
        <div className="font-bold mb-3">Security Status</div>
        <div className="space-y-2 text-sm">
          <div data-testid="status-password"><Dot ok={true}/>Password: <span className="text-slate-300">Configured (bcrypt)</span></div>
          <div data-testid="status-mfa"><Dot ok={status.mfa_enabled}/>Two-Factor Authentication: <span className={status.mfa_enabled ? "text-emerald-300 font-bold" : "text-amber-300 font-bold"}>{status.mfa_enabled ? "Enabled" : "Disabled"}</span></div>
          {status.mfa_enabled && <div><Dot ok={true}/>Authenticator: <span className="text-slate-300">{status.authenticator}</span></div>}
          {status.mfa_enabled && <div data-testid="status-recovery"><Dot ok={status.recovery_codes_remaining > 0}/>Recovery Codes: <span className="text-slate-300">{status.recovery_codes_remaining} remaining</span></div>}
          {status.last_mfa_verification && <div className="text-xs text-slate-500">Last MFA verification: {status.last_mfa_verification}</div>}
        </div>
        <div className="flex gap-2 flex-wrap mt-4">
          {!status.mfa_enabled && <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => setWizard("enable")} data-testid="mfa-enable-btn"><ShieldCheck size={14} className="mr-1"/>Enable Google Authenticator</Button>}
          {status.mfa_enabled && <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setWizard("change")} data-testid="mfa-change-btn"><RefreshCw size={14} className="mr-1"/>Change Google Authenticator</Button>}
          {status.mfa_enabled && <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setRegen(true)} data-testid="regen-codes-btn"><KeyRound size={14} className="mr-1"/>Regenerate Recovery Codes</Button>}
          {status.mfa_enabled && <Button variant="outline" className="border-red-500/40 text-red-400" onClick={() => setDisabling(true)} data-testid="mfa-disable-btn"><ShieldOff size={14} className="mr-1"/>Disable MFA</Button>}
          <Button variant="outline" className="border-slate-600 text-slate-300" onClick={() => setShowSessions(s => !s)} data-testid="manage-sessions-btn"><MonitorSmartphone size={14} className="mr-1"/>Manage Sessions</Button>
          <Button variant="outline" className="border-slate-600 text-slate-300" onClick={loadActivity} data-testid="view-activity-btn"><Activity size={14} className="mr-1"/>View Audit Log</Button>
        </div>
      </Card>

      {wizard && <MfaWizard mode={wizard} onClose={() => setWizard(null)} onDone={load}/>}
      {disabling && <DisableMfa onClose={() => setDisabling(false)} onDone={load}/>}

      {regen && !regenCodes && (
        <Card className="admin-card p-5 mb-4" data-testid="regen-panel">
          <div className="font-bold mb-2">Regenerate Recovery Codes</div>
          <p className="text-xs text-slate-400 mb-3">Requires your current password and a valid authenticator/recovery code. All previous codes become invalid immediately.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <Input type="password" placeholder="Current password" value={regenPw} onChange={e => setRegenPw(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="regen-pw"/>
            <Input placeholder="Authenticator or recovery code" value={regenCode} onChange={e => setRegenCode(e.target.value)} className="bg-slate-900 border-slate-700 font-mono" data-testid="regen-code"/>
          </div>
          <div className="flex gap-2 mt-3">
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={doRegen} data-testid="regen-submit">Regenerate</Button>
            <Button variant="ghost" className="text-slate-400" onClick={() => setRegen(false)}>Cancel</Button>
          </div>
        </Card>
      )}
      {regenCodes && <RecoveryCodesView codes={regenCodes} onDone={() => { setRegenCodes(null); setRegen(false); load(); }}/>}

      {showSessions && <div className="mb-4"><SessionsPanel/></div>}

      {showActivity && (
        <Card className="admin-card p-5 mb-4" data-testid="security-activity-panel">
          <div className="font-bold mb-3">Security Activity</div>
          <div className="space-y-1 text-xs font-mono max-h-72 overflow-y-auto">
            {activity.map(a => (
              <div key={a.id} className="flex gap-3 border-b border-slate-800 py-1">
                <span className="text-slate-500">{a.ts}</span>
                <span className="text-orange-300 font-bold">{a.action}</span>
              </div>
            ))}
            {activity.length === 0 && <div className="text-slate-500">No security events yet.</div>}
          </div>
        </Card>
      )}

      <Card className="admin-card p-5 mb-4" data-testid="security-identity-card">
        <div className="font-bold mb-3">Identity</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-300 text-xs">Email</Label>
            <Input value={email} onChange={e => setEmail(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="sec-email-input"/>
            <Label className="text-slate-300 text-xs mt-2">Confirm with Current Password</Label>
            <Input type="password" value={emailPw} onChange={e => setEmailPw(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="sec-email-pw-input"/>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 mt-2" onClick={changeEmail} data-testid="sec-change-email-btn">Update Email</Button>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Mobile Number</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="sec-phone-input"/>
            <Label className="text-slate-300 text-xs mt-2">Confirm with Current Password</Label>
            <Input type="password" value={phonePw} onChange={e => setPhonePw(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="sec-phone-pw-input"/>
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 mt-2" onClick={changePhone} data-testid="sec-change-phone-btn">Update Mobile</Button>
          </div>
        </div>
      </Card>

      <Card className="admin-card p-5">
        <div className="font-bold mb-3">Change Password</div>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label className="text-slate-300 text-xs">Current Password</Label><Input type="password" value={curPw} onChange={e => setCurPw(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="sec-cur-pw-input"/></div>
          <div><Label className="text-slate-300 text-xs">New Password</Label><Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="sec-new-pw-input"/></div>
          <div><Label className="text-slate-300 text-xs">Repeat New Password</Label><Input type="password" value={newPw2} onChange={e => setNewPw2(e.target.value)} className="bg-slate-900 border-slate-700" data-testid="sec-new-pw2-input"/></div>
        </div>
        <Button className="bg-orange-500 hover:bg-orange-600 mt-3" onClick={changePassword} data-testid="sec-change-password-btn">Change Password</Button>
        <p className="text-xs text-slate-500 mt-2">Min 10 chars with upper, lower and a number. All sessions are signed out after a password change.</p>
      </Card>
    </div>
  );
}
